/**
 * @file frontend/src/App.js
 * @module src
 * @component App
 *
 * App — Frontend module.
 *
 * Code-splitting strategy (Phase 5 follow-up):
 *   - Boot-critical modules (Login, Landing, layouts, auth context) are
 *     loaded eagerly so the first paint never waits on a chunk request.
 *   - Every other route is wrapped in React.lazy() so its JS chunk is
 *     fetched only when the user navigates to it.
 *   - <Suspense> with the same loader UI used by ProtectedRoute prevents
 *     a layout flash while a chunk downloads.
 *
 * Notes:
 *   - All API calls go through the shared axios client (REACT_APP_BACKEND_URL).
 *   - Side effects belong in `useEffect`; keep render pure.
 */
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { captureException } from "@sentry/react";
import { Toaster } from "./components/ui/sonner";

// Context (boot-critical)
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { canViewSubmodule } from "./lib/rbac";
import { WalkthroughProvider } from "./components/walkthrough/WalkthroughContext";

// Layouts (boot-critical — always rendered for the active route)
import DashboardLayout from "./layouts/DashboardLayout";
import AuthLayout from "./layouts/AuthLayout";

// Boot-critical pages (initial paint)
import LoginPage from "./pages/auth/LoginPage";
import LandingPage from "./pages/LandingPage";
import AuthCallback from "./pages/auth/AuthCallback";

// ---- Auth ----
const TwoFactorSetup = lazy(() => import("./pages/auth/TwoFactorSetup"));
const TwoFactorVerify = lazy(() => import("./pages/auth/TwoFactorVerify"));
const GoogleCallback = lazy(() => import("./pages/auth/GoogleCallback"));
const SAMLCallbackPage = lazy(() => import("./pages/auth/SAMLCallbackPage"));
const MicrosoftCallback = lazy(() => import("./pages/auth/MicrosoftCallback"));
const ForgotPasswordPage = lazy(() => import("./pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/auth/ResetPasswordPage"));

// ---- Dashboard ----
const DashboardPage = lazy(() => import("./pages/dashboard/DashboardPage"));
// TODO(concierge): AI Concierge hidden until integrations land / we have richer data to feed the LLM.
// Re-enable this import together with the route below, the nav item in DashboardLayout.js, and the
// aiConcierge tour in walkthroughData.js / TrainingCenter.js.
// const AIConcierge = lazy(() => import("./pages/dashboard/AIConcierge"));
const TrainingCenter = lazy(() => import("./pages/training/TrainingCenter"));
const RFPGenerator = lazy(() => import("./pages/admin/RFPGenerator"));
const SchoolPoliciesPage = lazy(() => import("./pages/admin/SchoolPoliciesPage"));
const DocumentationHub = lazy(() => import("./pages/admin/DocumentationHub"));
const PricingCalculator = lazy(() => import("./pages/admin/PricingCalculator"));
const SpecializedMarketplace = lazy(() => import("./pages/admin/SpecializedMarketplace"));
const TeletherapyCenter = lazy(() => import("./pages/admin/TeletherapyCenter"));
const UniversityPartnershipPortal = lazy(() => import("./pages/admin/UniversityPartnershipPortal"));

// ---- HR ----
const HRDashboard = lazy(() => import("./pages/hr/HRDashboard"));
const JobRequisitions = lazy(() => import("./pages/hr/JobRequisitions"));
const Applicants = lazy(() => import("./pages/hr/Applicants"));
const Employees = lazy(() => import("./pages/hr/Employees"));
const ReportingHierarchy = lazy(() => import("./pages/hr/ReportingHierarchy"));
const EmployeeExport = lazy(() => import("./pages/hr/EmployeeExport"));
const EmployeeOnboarding = lazy(() => import("./pages/hr/EmployeeOnboarding"));
const Certifications = lazy(() => import("./pages/hr/Certifications"));
const SubstituteManagement = lazy(() => import("./pages/hr/SubstituteManagement"));
const PositionControl = lazy(() => import("./pages/hr/PositionControl"));
const ProfessionalDevelopment = lazy(() => import("./pages/hr/ProfessionalDevelopment"));
const PerformanceEvaluations = lazy(() => import("./pages/hr/PerformanceEvaluations"));
const ContractTenure = lazy(() => import("./pages/hr/ContractTenure"));
const ExitInterviews = lazy(() => import("./pages/hr/ExitInterviews"));
const EmployeeSelfService = lazy(() => import("./pages/hr/EmployeeSelfService"));
const AITools = lazy(() => import("./pages/hr/AITools"));
const MyProfile = lazy(() => import("./pages/hr/MyProfile"));
const MyCertifications = lazy(() => import("./pages/hr/MyCertifications"));
const MyDocuments = lazy(() => import("./pages/hr/MyDocuments"));
const SalarySchedules = lazy(() => import("./pages/hr/SalarySchedules"));
const SeniorityList = lazy(() => import("./pages/hr/SeniorityList"));
const TotalCompensation = lazy(() => import("./pages/hr/TotalCompensation"));

// ---- Hiring ----
const HiringDashboard = lazy(() => import("./pages/hiring/HiringDashboard"));
const HiringExport = lazy(() => import("./pages/hiring/HiringExport"));
const Interviews = lazy(() => import("./pages/hiring/Interviews"));
const OfferLetters = lazy(() => import("./pages/hiring/OfferLetters"));
const BackgroundChecks = lazy(() => import("./pages/hiring/BackgroundChecks"));
const NewHireChecklist = lazy(() => import("./pages/hiring/NewHireChecklist"));
const MyCalendar = lazy(() => import("./pages/attendance/MyCalendar"));
const CalendarManagement = lazy(() => import("./pages/attendance/CalendarManagement"));

// ---- Settings ----
const IntegrationSettings = lazy(() => import("./pages/settings/IntegrationSettings"));
const InfiniteVisionsIntegration = lazy(() => import("./pages/settings/InfiniteVisionsIntegration"));


// ---- Attendance ----
const AttendanceDashboard = lazy(() => import("./pages/attendance/AttendanceDashboard"));
const TimesheetApprovals = lazy(() => import("./pages/attendance/TimesheetApprovals"));
const GrantApprovalQueue = lazy(() => import("./pages/attendance/GrantApprovalQueue"));
const MyTimesheet = lazy(() => import("./pages/attendance/MyTimesheet"));
const TimeRulesConfig = lazy(() => import("./pages/attendance/TimeRulesConfig"));
const AIAnalytics = lazy(() => import("./pages/attendance/AIAnalytics"));
const SmartScheduling = lazy(() => import("./pages/attendance/SmartScheduling"));
const BurnoutRisk = lazy(() => import("./pages/attendance/BurnoutRisk"));
const GeofencingAlerts = lazy(() => import("./pages/attendance/GeofencingAlerts"));
const TimeFraudDetection = lazy(() => import("./pages/attendance/TimeFraudDetection"));
const VoiceClock = lazy(() => import("./pages/attendance/VoiceClock"));
const WellnessIntegration = lazy(() => import("./pages/attendance/WellnessIntegration"));
const SmartTimeRounding = lazy(() => import("./pages/attendance/SmartTimeRounding"));
const OvertimePrediction = lazy(() => import("./pages/attendance/OvertimePrediction"));
const AutomatedCompliance = lazy(() => import("./pages/attendance/AutomatedCompliance"));
const SmartSavingsDashboard = lazy(() => import("./pages/attendance/SmartSavingsDashboard"));
const FMLAManagement = lazy(() => import("./pages/attendance/FMLAManagement"));
const UnionContractRules = lazy(() => import("./pages/attendance/UnionContractRules"));
const AbsenceForecast = lazy(() => import("./pages/attendance/AbsenceForecast"));
const CostPerAbsence = lazy(() => import("./pages/attendance/CostPerAbsence"));
const StaffSharing = lazy(() => import("./pages/attendance/StaffSharing"));
const ISBEReports = lazy(() => import("./pages/attendance/ISBEReports"));
const BIPACompliance = lazy(() => import("./pages/attendance/BIPACompliance"));
const PayRulesConfig = lazy(() => import("./pages/attendance/PayRulesConfig"));
const SubstituteCallingSystem = lazy(() => import("./pages/attendance/SubstituteCallingSystem"));
const AdvancedScheduling = lazy(() => import("./pages/attendance/AdvancedScheduling"));
const ReportingAnalytics = lazy(() => import("./pages/attendance/ReportingAnalytics"));
const DeviceIntegrations = lazy(() => import("./pages/attendance/integrations/DeviceIntegrations"));
const MobileClockIn = lazy(() => import("./pages/attendance/MobileClockIn"));
const ManagerGeofenceDashboard = lazy(() => import("./pages/attendance/ManagerGeofenceDashboard"));
const IPAllowlist = lazy(() => import("./pages/attendance/IPAllowlist"));
const EmployeeTimesheets = lazy(() => import("./pages/attendance/EmployeeTimesheets"));
const SupervisorExceptionInbox = lazy(() => import("./pages/attendance/SupervisorExceptionInbox"));
const RequestsHub = lazy(() => import("./pages/attendance/RequestsHub"));


// ---- Onboarding ----
const OnboardingDashboard = lazy(() => import("./pages/onboarding/OnboardingDashboard"));
const EmployeeOnboardingWizard = lazy(() => import("./pages/onboarding/EmployeeOnboardingWizard"));


// ---- Admin ----
const TenantManagement = lazy(() => import("./pages/admin/TenantManagement"));
const TenantModuleManagement = lazy(() => import("./pages/admin/TenantModuleManagement"));
const NewDistrictPilotWizard = lazy(() => import("./pages/admin/NewDistrictPilotWizard"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const MaintenancePage = lazy(() => import("./pages/admin/MaintenancePage"));
const SeedManagement = lazy(() => import("./pages/admin/SeedManagement"));
const SuperAdminDashboard = lazy(() => import("./pages/admin/SuperAdminDashboard"));
const RulesEngine = lazy(() => import("./pages/admin/RulesEngine"));
const RBACManagement = lazy(() => import("./pages/admin/RBACManagement"));
const IntegrationsHub = lazy(() => import("./pages/admin/IntegrationsHub"));
const IdentityOnboardWizard = lazy(() => import("./pages/admin/IdentityOnboardWizard"));
const PilotDiscoveryAdmin = lazy(() => import("./pages/admin/PilotDiscoveryAdmin"));
const PilotDiscoveryPublicPage = lazy(() => import("./pages/PilotDiscoveryPublicPage"));
const IdentityRecommendationSharePage = lazy(() => import("./pages/IdentityRecommendationSharePage"));
const StartDiscoveryPage = lazy(() => import("./pages/StartDiscoveryPage"));
const SAMLIntegrationPage = lazy(() => import("./pages/admin/SAMLIntegrationPage"));
const LDAPIntegrationPage = lazy(() => import("./pages/admin/LDAPIntegrationPage"));
const SFTPIntegrationPage = lazy(() => import("./pages/admin/SFTPIntegrationPage"));
const PositionMapping = lazy(() => import("./pages/admin/PositionMapping"));
const AttendanceRuleSets = lazy(() => import("./pages/admin/AttendanceRuleSets"));
const AbsenceTypes = lazy(() => import("./pages/admin/AbsenceTypes"));
const PayrollLeaveBalances = lazy(() => import("./pages/admin/PayrollLeaveBalances"));

// ---- Phase 2-5 Admin ----
const SimulationDashboard = lazy(() => import("./pages/admin/simulation/SimulationDashboard"));
const DiagnosticsDashboard = lazy(() => import("./pages/admin/diagnostics/DiagnosticsDashboard"));
const ConfigVersioningDashboard = lazy(() => import("./pages/admin/config/ConfigVersioningDashboard"));
const OperationsDashboard = lazy(() => import("./pages/admin/operations/OperationsDashboard"));
const IdentityHub = lazy(() => import("./pages/admin/IdentityHub"));
const GroupRoleMapping = lazy(() => import("./pages/admin/GroupRoleMapping"));
const RoleHierarchyMapping = lazy(() => import("./pages/admin/RoleHierarchyMapping"));
const DistrictSetup = lazy(() => import("./pages/admin/DistrictSetup"));
const UserLookup = lazy(() => import("./pages/admin/UserLookup"));
const IdentityReconciliation = lazy(() => import("./pages/admin/IdentityReconciliation"));
const ComplianceExport = lazy(() => import("./pages/admin/ComplianceExport"));
const WebhookSubscriptions = lazy(() => import("./pages/admin/WebhookSubscriptions"));
const ConditionalAccessPolicies = lazy(() => import("./pages/admin/ConditionalAccessPolicies"));
const FieldVisibilityPolicy = lazy(() => import("./pages/admin/FieldVisibilityPolicy"));
const ImportsCenter = lazy(() => import("./pages/imports/ImportsCenter"));
const BugFixing = lazy(() => import("./pages/admin/BugFixing"));


// ---- Multi-District Expansion (Master Admin / Billing / Feature Flags) ----
const MasterAdminDashboard = lazy(() => import("./pages/admin/multi_district/MasterAdminDashboard"));
const BillingInvoices = lazy(() => import("./pages/admin/multi_district/BillingInvoices"));
const FeatureFlagsAdmin = lazy(() => import("./pages/admin/multi_district/FeatureFlagsAdmin"));

// ---- Direct Competition (DIRECT_COMPETITION_SCOPE.pdf) ----
const DirectCompetitionHub = lazy(() => import("./pages/admin/direct_competition/DirectCompetitionHub"));
const FounderActionItems = lazy(() => import("./pages/admin/direct_competition/FounderActionItems"));
const AuditDefenseCenter = lazy(() => import("./pages/admin/direct_competition/AuditDefenseCenter"));

const CustomerSuccessDashboard = lazy(() => import("./pages/admin/direct_competition/CustomerSuccessDashboard"));
const SandboxProvisioning = lazy(() => import("./pages/admin/direct_competition/SandboxProvisioning"));
const AICapabilitiesStudio = lazy(() => import("./pages/admin/direct_competition/AICapabilitiesStudio"));
const StateDriverRegistry = lazy(() => import("./pages/admin/direct_competition/StateDriverRegistry"));
const PricingTransparency = lazy(() => import("./pages/admin/direct_competition/PricingTransparency"));


// ============================================================================
// Shared loading UI
// ============================================================================
const FullScreenLoader = ({ label = "Loading..." }) => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="route-loader">
    <div className="animate-pulse flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      <p className="text-slate-600 font-medium">{label}</p>
    </div>
  </div>
);


// ============================================================================
// General error boundary — catches render/lifecycle errors not related to chunk
// loading. Reports to Sentry and shows a recoverable UI instead of a white screen.
// ============================================================================
class AppErrorBoundary extends React.Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    captureException(error, { extra: { componentStack: info.componentStack } });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-6 text-center">
        <h2 className="text-2xl font-semibold text-slate-900 mb-2">Something went wrong</h2>
        <p className="text-slate-600 mb-6 max-w-md">
          The error has been reported. Try refreshing the page.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-5 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700"
        >
          Try again
        </button>
      </div>
    );
  }
}

// Chunk-load error boundary
// Catches `ChunkLoadError` thrown by React.lazy() when a stale deploy serves
// a hashed bundle that no longer exists on the CDN. We prompt a one-tap
// hard refresh — without this, the entire Suspense subtree would white-screen.
// ============================================================================
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    // Only auto-reload on chunk-load failures — anything else surfaces the UI.
    const isChunkError =
      error?.name === 'ChunkLoadError' ||
      /Loading chunk \d+ failed/i.test(error?.message || '') ||
      /Failed to fetch dynamically imported module/i.test(error?.message || '');
    if (isChunkError && !sessionStorage.getItem('chunk_reload_attempted')) {
      sessionStorage.setItem('chunk_reload_attempted', '1');
      window.location.reload();
    }
  }

  handleReload = () => {
    sessionStorage.removeItem('chunk_reload_attempted');
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-6 text-center"
        data-testid="chunk-error-boundary"
      >
        <h2 className="text-2xl font-semibold text-slate-900 mb-2">
          Something went wrong loading this page
        </h2>
        <p className="text-slate-600 mb-6 max-w-md">
          A new version of the application may have been deployed. Reload to get the latest build.
        </p>
        <button
          onClick={this.handleReload}
          className="px-5 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700"
          data-testid="chunk-error-reload-button"
        >
          Reload
        </button>
      </div>
    );
  }
}


// ============================================================================
// Protected Route Component
// ============================================================================
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <FullScreenLoader />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Phase 6 — mandatory 2FA setup gate. If the user hasn't completed 2FA setup
  // yet, force them to /setup-2fa before they can hit any other protected page.
  if (user.tfa_setup_required && location.pathname !== '/setup-2fa') {
    return <Navigate to="/setup-2fa" replace />;
  }

  return children;
};

// Admin-only Route Guard - requires IT_ADMIN or super_admin
const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  const [roles, setRoles] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchRoles = async () => {
      try {
        const { api } = await import('./lib/api');
        const response = await api.get('/rbac/me/');
        setRoles(response.data?.roles || []);
      } catch {
        setRoles([]);
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchRoles();
    else setLoading(false);
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isAdmin = user?.is_super_admin ||
    (roles && roles.includes('IT_ADMIN'));

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-4" data-testid="forbidden-message">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">403 — Not Authorized</h2>
        <p className="text-slate-500 max-w-md">You do not have permission to access this page. Contact your district administrator if you believe this is an error.</p>
      </div>
    );
  }

  return children;
};


// Registry-submodule Route Guard — the permission-driven successor to the
// role-based guards above. Gates a page by the RBAC registry's submodule
// `.view` key (e.g. submodule="admin.rbac" ⇒ requires "admin.rbac.view") — the
// SAME key the backend enforces via RequireSubmodule and the sidebar uses to
// show/hide the nav item. Super-admins bypass; everyone else must hold the key
// in /rbac/me/. This aligns the frontend with backend gating so a user can't
// deep-link to a page whose APIs will just 403.
const RequireSubmodule = ({ submodule, children }) => {
  const { user } = useAuth();
  const [permissions, setPermissions] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchPerms = async () => {
      try {
        const { api } = await import('./lib/api');
        const response = await api.get('/rbac/me/');
        setPermissions(response.data?.permissions || []);
      } catch {
        setPermissions([]);
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchPerms();
    else setLoading(false);
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const allowed = canViewSubmodule(submodule, {
    permissions: permissions || [],
    isSuperAdmin: user?.is_super_admin,
    roles: user?.roles || [],
  });
  if (!allowed) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-4" data-testid="forbidden-message">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">403 — Not Authorized</h2>
        <p className="text-slate-500 max-w-md">You do not have permission to access this page. Contact your district administrator if you believe this is an error.</p>
      </div>
    );
  }

  return children;
};


// ============================================================================
// App Router
// ============================================================================
const AppRouter = () => {
  const location = useLocation();

  // Handle OAuth callback (eager — used immediately on app boot via URL hash)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<FullScreenLoader />}>
        <Routes>
        {/* Public Landing Page */}
        <Route path="/" element={<LandingPage />} />

        {/* Public Routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/google" element={<GoogleCallback />} />
          <Route path="/setup-2fa" element={<ProtectedRoute><TwoFactorSetup /></ProtectedRoute>} />
          <Route path="/verify-2fa" element={<TwoFactorVerify />} />
          <Route path="/auth/saml-callback" element={<SAMLCallbackPage />} />
        </Route>

        {/* Public share pages — full-width, no auth layout */}
        <Route path="/share/discovery/:token" element={<PilotDiscoveryPublicPage />} />
        <Route path="/share/identity-recommendation" element={<IdentityRecommendationSharePage />} />
        <Route path="/start-discovery" element={<StartDiscoveryPage />} />

        {/* Microsoft OAuth Callback */}
        <Route path="/auth/microsoft/callback" element={<MicrosoftCallback />} />

        {/* Protected Routes */}
        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          {/* Main Dashboard */}
          <Route path="/" element={<DashboardPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          {/* TODO(concierge): hidden until integrations/data ready — see import above. */}
          {/* <Route path="/concierge" element={<AIConcierge />} /> */}

          {/* Hiring & Onboarding Module */}
          <Route path="/hiring" element={<RequireSubmodule submodule="hiring.dashboard"><HiringDashboard /></RequireSubmodule>} />
          <Route path="/hiring/requisitions" element={<RequireSubmodule submodule="hiring.applicant_tracking_system"><JobRequisitions /></RequireSubmodule>} />
          <Route path="/hiring/applicants" element={<RequireSubmodule submodule="hiring.applicant_tracking_system"><Applicants /></RequireSubmodule>} />
          <Route path="/hiring/interviews" element={<RequireSubmodule submodule="hiring.applicant_tracking_system"><Interviews /></RequireSubmodule>} />
          <Route path="/hiring/offers" element={<RequireSubmodule submodule="hiring.offers_e_signature"><OfferLetters /></RequireSubmodule>} />
          <Route path="/hiring/background-checks" element={<RequireSubmodule submodule="hiring.background_checks"><BackgroundChecks /></RequireSubmodule>} />
          <Route path="/hiring/checklist" element={<RequireSubmodule submodule="hiring.hire_onboarding"><NewHireChecklist /></RequireSubmodule>} />
          <Route path="/hiring/export" element={<RequireSubmodule submodule="hiring.export_to_hr"><HiringExport /></RequireSubmodule>} />

          {/* HR Module */}
          <Route path="/hr" element={<HRDashboard />} />
          <Route path="/hr/employees" element={<Employees />} />
          <Route path="/hr/employees/:employeeId/onboarding" element={<EmployeeOnboarding />} />
          <Route path="/hr/reporting-hierarchy" element={<ReportingHierarchy />} />
          <Route path="/hr/export" element={<EmployeeExport />} />
          <Route path="/hr/certifications" element={<Certifications />} />
          <Route path="/hr/substitutes" element={<SubstituteManagement />} />
          <Route path="/hr/positions" element={<PositionControl />} />
          <Route path="/hr/professional-development" element={<ProfessionalDevelopment />} />
          <Route path="/hr/evaluations" element={<PerformanceEvaluations />} />
          <Route path="/hr/contracts" element={<ContractTenure />} />
          <Route path="/hr/salary-schedules" element={<SalarySchedules />} />
          <Route path="/hr/seniority" element={<SeniorityList />} />
          <Route path="/hr/total-compensation" element={<TotalCompensation />} />
          <Route path="/hr/exit-interviews" element={<ExitInterviews />} />
          <Route path="/hr/my-dashboard" element={<EmployeeSelfService />} />
          <Route path="/hr/ai-tools" element={<AITools />} />
          {/* Employee Self-Service HR Routes */}
          <Route path="/hr/my-profile" element={<RequireSubmodule submodule="attendance.my_profile"><MyProfile /></RequireSubmodule>} />
          <Route path="/hr/my-certifications" element={<MyCertifications />} />
          <Route path="/hr/my-documents" element={<RequireSubmodule submodule="attendance.my_documents"><MyDocuments /></RequireSubmodule>} />
          <Route path="/hr/my-training" element={<ProfessionalDevelopment />} />
          <Route path="/hr/my-evaluations" element={<PerformanceEvaluations />} />
          <Route path="/hr/my-career" element={<EmployeeSelfService />} />
          <Route path="/hr/my-development" element={<ProfessionalDevelopment />} />

          {/* Settings */}
          <Route path="/settings/integrations" element={<IntegrationSettings />} />

          {/* Attendance Module */}
          <Route path="/attendance" element={<RequireSubmodule submodule="attendance.supervisor_inbox"><AttendanceDashboard /></RequireSubmodule>} />
          <Route path="/attendance/my-timesheet" element={<RequireSubmodule submodule="attendance.my_timesheet"><MyTimesheet /></RequireSubmodule>} />
          <Route path="/attendance/approvals" element={<RequireSubmodule submodule="attendance.supervisor_inbox"><TimesheetApprovals /></RequireSubmodule>} />
          {/* Grant Approvals — gated on GrantApprover roster membership, not
              a permission key (see is_grant_approver from /rbac/me/). The
              page itself renders a 403-style message if the backend rejects
              a non-approver, matching RequireSubmodule's convention. */}
          <Route path="/attendance/grant-approvals" element={<GrantApprovalQueue />} />
          <Route path="/attendance/exceptions" element={<Navigate to="/attendance/approvals" replace />} />
          <Route path="/attendance/requests" element={<RequireSubmodule submodule="attendance.authorization_requests"><RequestsHub /></RequireSubmodule>} />
          <Route path="/attendance/rules" element={<RequireSubmodule submodule="attendance.time_rules"><TimeRulesConfig /></RequireSubmodule>} />
          {/* AI-Powered Time & Attendance Features */}
          <Route path="/attendance/ai-analytics" element={<AIAnalytics />} />
          <Route path="/attendance/smart-scheduling" element={<SmartScheduling />} />
          <Route path="/attendance/smart-savings" element={<RequireSubmodule submodule="attendance.ai_tools"><SmartSavingsDashboard /></RequireSubmodule>} />
          <Route path="/attendance/burnout-risk" element={<BurnoutRisk />} />
          <Route path="/attendance/geofencing" element={<RequireSubmodule submodule="attendance.geofence_zones"><GeofencingAlerts /></RequireSubmodule>} />
          <Route path="/attendance/fraud-detection" element={<TimeFraudDetection />} />
          <Route path="/attendance/voice-clock" element={<VoiceClock />} />
          <Route path="/attendance/wellness" element={<WellnessIntegration />} />
          <Route path="/attendance/time-rounding" element={<SmartTimeRounding />} />
          <Route path="/attendance/overtime-prediction" element={<OvertimePrediction />} />
          <Route path="/attendance/compliance" element={<AutomatedCompliance />} />
          {/* FMLA & Substitute Management (RFP Phase 1) */}
          <Route path="/attendance/fmla" element={<RequireSubmodule submodule="attendance.fmla"><FMLAManagement /></RequireSubmodule>} />
          <Route path="/attendance/substitute-calling" element={<RequireSubmodule submodule="attendance.substitute_management"><SubstituteCallingSystem /></RequireSubmodule>} />
          {/* Advanced Scheduling & Reporting (RFP Phase 2 & 3) */}
          <Route path="/attendance/advanced-scheduling" element={<RequireSubmodule submodule="attendance.scheduling"><AdvancedScheduling /></RequireSubmodule>} />
          <Route path="/attendance/reporting" element={<RequireSubmodule submodule="attendance.reports_analytics"><ReportingAnalytics /></RequireSubmodule>} />
          <Route path="/attendance/union-rules" element={<RequireSubmodule submodule="hiring.union_contract_rules"><UnionContractRules /></RequireSubmodule>} />
          <Route path="/hiring/union-rules" element={<RequireSubmodule submodule="hiring.union_contract_rules"><UnionContractRules /></RequireSubmodule>} />
          {/* CBARuleConfiguration merged into the unified Rule Set Assignment module */}
          <Route path="/hiring/cba-rules" element={<Navigate to="/admin/attendance-rule-sets" replace />} />
          <Route path="/attendance/absence-forecast" element={<RequireSubmodule submodule="attendance.ai_tools"><AbsenceForecast /></RequireSubmodule>} />
          <Route path="/attendance/cost-analysis" element={<RequireSubmodule submodule="attendance.reports_analytics"><CostPerAbsence /></RequireSubmodule>} />
          <Route path="/attendance/staff-sharing" element={<RequireSubmodule submodule="attendance.staff_sharing"><StaffSharing /></RequireSubmodule>} />
          <Route path="/attendance/state-reports" element={<RequireSubmodule submodule="attendance.compliance_isbe"><ISBEReports /></RequireSubmodule>} />
          <Route path="/attendance/bipa" element={<RequireSubmodule submodule="attendance.compliance_isbe"><BIPACompliance /></RequireSubmodule>} />
          <Route path="/attendance/pay-rules" element={<RequireSubmodule submodule="attendance.pay_rules"><PayRulesConfig /></RequireSubmodule>} />
          <Route path="/hiring/calendar" element={<RequireSubmodule submodule="hiring.calendar"><CalendarManagement /></RequireSubmodule>} />
          <Route path="/hiring/my-calendar" element={<RequireSubmodule submodule="hiring.my_calendar"><MyCalendar /></RequireSubmodule>} />
          <Route path="/settings/integrations/infinite-visions" element={<InfiniteVisionsIntegration />} />
          {/* IP Allowlist — IT Director only */}
          <Route path="/attendance/ip-allowlist" element={<RequireSubmodule submodule="attendance.ip_allowlist"><IPAllowlist /></RequireSubmodule>} />
          {/* Device & Vendor Integrations */}
          <Route path="/attendance/integrations" element={<RequireSubmodule submodule="attendance.device_integrations"><DeviceIntegrations /></RequireSubmodule>} />
          {/* Geofencing & Mobile */}
          <Route path="/attendance/mobile-clock" element={<RequireSubmodule submodule="attendance.my_timesheet"><MobileClockIn /></RequireSubmodule>} />
          <Route path="/attendance/manager-dashboard" element={<ManagerGeofenceDashboard />} />
          <Route path="/attendance/employee-timesheets" element={<RequireSubmodule submodule="attendance.timesheets_exceptions"><EmployeeTimesheets /></RequireSubmodule>} />
          <Route path="/attendance/exception-inbox" element={<RequireSubmodule submodule="attendance.supervisor_inbox"><SupervisorExceptionInbox /></RequireSubmodule>} />
          {/* Supervisor "My Team" Routes */}
          <Route path="/attendance/team" element={<Navigate to="/attendance/team-timesheets" replace />} />
          <Route path="/attendance/team-timesheets" element={<EmployeeTimesheets />} />
          <Route path="/attendance/approve" element={<Navigate to="/attendance/approvals" replace />} />
          <Route path="/attendance/team-schedule" element={<AdvancedScheduling />} />
          <Route path="/attendance/team-exceptions" element={<RequireSubmodule submodule="attendance.supervisor_inbox"><SupervisorExceptionInbox /></RequireSubmodule>} />
          {/* Employee Self-Service Attendance Routes */}
          <Route path="/attendance/clock" element={<RequireSubmodule submodule="attendance.my_timesheet"><MyTimesheet /></RequireSubmodule>} />
          <Route path="/attendance/my-time-off" element={<RequireSubmodule submodule="attendance.leave"><EmployeeSelfService /></RequireSubmodule>} />
          <Route path="/attendance/my-balances" element={<RequireSubmodule submodule="attendance.leave"><EmployeeSelfService /></RequireSubmodule>} />

          {/* Onboarding Module */}
          <Route path="/onboarding" element={<RequireSubmodule submodule="hiring.hire_onboarding"><OnboardingDashboard /></RequireSubmodule>} />
          <Route path="/onboarding/my-onboarding" element={<RequireSubmodule submodule="hiring.my_onboarding"><EmployeeOnboardingWizard /></RequireSubmodule>} />

          {/* Training Center */}
          <Route path="/training" element={<TrainingCenter />} />
          <Route path="/rfp" element={<RFPGenerator />} />
          <Route path="/pricing" element={<PricingCalculator />} />

          {/* Admin */}
          <Route path="/admin" element={<SuperAdminDashboard />} />
          <Route path="/admin/tenants" element={<TenantManagement />} />
          <Route path="/admin/tenants/new-pilot" element={<NewDistrictPilotWizard />} />
          <Route path="/admin/tenants/:tenantId/modules" element={<TenantModuleManagement />} />
          <Route path="/admin/users" element={<RequireSubmodule submodule="admin.user_management"><UserManagement /></RequireSubmodule>} />
          <Route path="/admin/rules" element={<RulesEngine />} />
          <Route path="/admin/rbac" element={<RequireSubmodule submodule="admin.rbac"><RBACManagement /></RequireSubmodule>} />
          <Route path="/admin/maintenance" element={<MaintenancePage />} />
          <Route path="/admin/integrations" element={<RequireSubmodule submodule="admin.integrations_hub"><IntegrationsHub /></RequireSubmodule>} />
          <Route path="/admin/identity/onboard" element={<RequireSubmodule submodule="admin.identity_sso"><IdentityOnboardWizard /></RequireSubmodule>} />
          <Route path="/admin/discovery" element={<RequireSubmodule submodule="admin.pilot_discovery"><PilotDiscoveryAdmin /></RequireSubmodule>} />
          <Route path="/admin/identity/saml" element={<RequireSubmodule submodule="admin.identity_sso"><SAMLIntegrationPage /></RequireSubmodule>} />
          <Route path="/admin/identity/ldap" element={<RequireSubmodule submodule="admin.identity_sso"><LDAPIntegrationPage /></RequireSubmodule>} />
          <Route path="/admin/attendance/sftp" element={<RequireSubmodule submodule="admin.sftp_time_clock"><SFTPIntegrationPage /></RequireSubmodule>} />
          <Route path="/admin/seed-management" element={<SeedManagement />} />
          <Route path="/admin/rfp" element={<RFPGenerator />} />
          <Route path="/admin/policies" element={<SchoolPoliciesPage />} />
          <Route path="/admin/school-policies" element={<SchoolPoliciesPage />} />
          <Route path="/admin/docs" element={<DocumentationHub />} />
          <Route path="/admin/pricing" element={<PricingCalculator />} />
          <Route path="/admin/specialized-marketplace" element={<SpecializedMarketplace />} />
          <Route path="/admin/teletherapy" element={<TeletherapyCenter />} />
          <Route path="/admin/university-partnerships" element={<UniversityPartnershipPortal />} />
          {/* <Route path="/services" element={<DistrictServicesHub />} /> */}
          <Route path="/admin/position-mapping" element={<RequireSubmodule submodule="admin.position_mapping"><PositionMapping /></RequireSubmodule>} />
          <Route path="/admin/attendance-rule-sets" element={<RequireSubmodule submodule="attendance.rule_set_assignment"><AttendanceRuleSets /></RequireSubmodule>} />
          <Route path="/admin/absence-types" element={<RequireSubmodule submodule="attendance.absence_types"><AbsenceTypes /></RequireSubmodule>} />
          <Route path="/admin/payroll-leave-balances" element={<RequireSubmodule submodule="attendance.absence_types"><PayrollLeaveBalances /></RequireSubmodule>} />

          {/* Phase 2-5 Admin Tools */}
          <Route path="/admin/simulation" element={<SimulationDashboard />} />
          <Route path="/admin/diagnostics" element={<DiagnosticsDashboard />} />
          <Route path="/admin/config" element={<ConfigVersioningDashboard />} />
          <Route path="/admin/operations" element={<OperationsDashboard />} />
          <Route path="/admin/identity-hub" element={<RequireSubmodule submodule="admin.identity_sso"><IdentityHub /></RequireSubmodule>} />
          <Route path="/admin/group-role-mapping" element={<RequireSubmodule submodule="admin.group_role_mapping"><GroupRoleMapping /></RequireSubmodule>} />
          <Route path="/admin/role-hierarchy" element={<RequireSubmodule submodule="admin.role_hierarchy"><RoleHierarchyMapping /></RequireSubmodule>} />
          <Route path="/admin/district-setup" element={<RequireSubmodule submodule="admin.district_setup"><DistrictSetup /></RequireSubmodule>} />
          <Route path="/admin/user-lookup" element={<AdminRoute><UserLookup /></AdminRoute>} />
          <Route path="/admin/identity-reconciliation" element={<AdminRoute><IdentityReconciliation /></AdminRoute>} />
          <Route path="/admin/compliance-export" element={<AdminRoute><ComplianceExport /></AdminRoute>} />
          <Route path="/admin/webhooks" element={<AdminRoute><WebhookSubscriptions /></AdminRoute>} />
          <Route path="/admin/conditional-access" element={<AdminRoute><ConditionalAccessPolicies /></AdminRoute>} />
          <Route path="/admin/field-visibility" element={<AdminRoute><FieldVisibilityPolicy /></AdminRoute>} />
          <Route path="/imports" element={<ImportsCenter />} />
          <Route path="/admin/bug-fixing" element={<BugFixing />} />

          {/* Multi-District Expansion admin pages (WP-A4 / WP-B4 / WP-C1) */}
          <Route path="/admin/master-admin/dashboard" element={<AdminRoute><MasterAdminDashboard /></AdminRoute>} />
          <Route path="/admin/billing/invoices" element={<AdminRoute><BillingInvoices /></AdminRoute>} />
          <Route path="/admin/feature-flags" element={<AdminRoute><FeatureFlagsAdmin /></AdminRoute>} />

          {/* Direct Competition admin pages (DIRECT_COMPETITION_SCOPE.pdf) */}
          <Route path="/admin/direct-competition" element={<AdminRoute><DirectCompetitionHub /></AdminRoute>} />
          <Route path="/admin/direct-competition/founder-actions" element={<AdminRoute><FounderActionItems /></AdminRoute>} />
          <Route path="/admin/direct-competition/audit-defense" element={<AdminRoute><AuditDefenseCenter /></AdminRoute>} />
          <Route path="/admin/direct-competition/customer-success" element={<AdminRoute><CustomerSuccessDashboard /></AdminRoute>} />
          <Route path="/admin/direct-competition/sandbox" element={<AdminRoute><SandboxProvisioning /></AdminRoute>} />
          <Route path="/admin/direct-competition/ai-studio" element={<AdminRoute><AICapabilitiesStudio /></AdminRoute>} />
          <Route path="/admin/direct-competition/state-drivers" element={<AdminRoute><StateDriverRegistry /></AdminRoute>} />
          <Route path="/admin/direct-competition/pricing" element={<AdminRoute><PricingTransparency /></AdminRoute>} />

          {/* Settings — folded into MyProfile as a tab */}
          <Route path="/settings" element={<Navigate to="/hr/my-profile?tab=settings" replace />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
    </ChunkErrorBoundary>
  );
};

function App() {
  return (
    <AppErrorBoundary>
    <BrowserRouter>
      <AuthProvider>
          <WalkthroughProvider>
            <AppRouter />
          <Toaster
            position="top-right"
            richColors
            closeButton
            expand
            toastOptions={{
              duration: 5000,
              style: {
                marginTop: '60px',
              },
            }}
          />
          </WalkthroughProvider>
      </AuthProvider>
    </BrowserRouter>
    </AppErrorBoundary>
  );
}

export default App;
